import type React from "react"

import { useState, useRef, useEffect } from "react"
import { useFormContext } from "react-hook-form"
import { ChevronDown, X } from "lucide-react"
import "./SingleFancyDropdown.css"

interface Option {
  Id: string | number
  Name: string
}

interface SingleSelectDropdownProps {
  name: string
  label: string
  options: Option[]
  required?: boolean
  requiredMessage?: string
  placeholderText?: string
  defaultValue?: string | number
  validation?: {
    required?: string | boolean
    [key: string]: any
  }
  className?: string
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void
  disabled?: boolean
  isTags?:boolean
}

export default function SingleSelectDropdown({
  name,
  label,
  required = false,
  options,
  requiredMessage = "Please select an option",
  placeholderText = "Select an option",
  validation = {},
  defaultValue = "",
  className = "",
  onChange,
  disabled = false,
  isTags = false,
  ...rest
}: SingleSelectDropdownProps) {
  const {
    register,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Watch the value from react-hook-form
  const selectedValue = watch(name)

  // Find the selected option object
  const selectedOption = options.find((option) => option.Id === selectedValue)

  useEffect(() => {
    // Set default value if provided
    if (defaultValue) {
      setValue(name, defaultValue)
    }
  }, [defaultValue, name, setValue])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Register the field with react-hook-form
  const { ref, ...registerProps } = register(name, {
    required: required ? requiredMessage : false,
    ...validation,
    // Remove onChange from here since we handle it manually
  })

  const handleSelectOption = (option: Option) => {
    setValue(name, option.Id, { shouldValidate: true })
    setIsOpen(false)

    // Call onChange if provided
    if (onChange) {
      // Create a synthetic event object similar to a select element
      const syntheticEvent = {
        target: {
          name,
          value: option.Id,
          selectedOptions: [{ text: option.Name, value: option.Id }],
        },
      } as unknown as React.ChangeEvent<HTMLSelectElement>

      onChange(syntheticEvent)
    }
  }

  const handleRemoveSelection = () => {
    setValue(name, "", { shouldValidate: true })

    // Call onChange if provided
    if (onChange) {
      // Create a synthetic event object for empty selection
      const syntheticEvent = {
        target: {
          name,
          value: "",
          selectedOptions: [],
        },
      } as unknown as React.ChangeEvent<HTMLSelectElement>

      onChange(syntheticEvent)
    }
  }

  return (
    <div className={`single-select-container ${className}`}>
      <label className="single-select-label black">
        {label}
        {required && <span className="required-asterisk">*</span>}
      </label>

      <div className="single-select-wrapper" ref={dropdownRef}>
        <div
          className={`single-select-trigger ${isOpen ? "open" : ""} ${errors[name] ? "error" : ""} ${disabled ? "disabled" : ""}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <span className={`single-select-placeholder ${selectedValue ? "has-value" : ""}`}>
            {selectedOption ? selectedOption.Name : placeholderText}
          </span>
          <ChevronDown className={`single-select-arrow ${isOpen ? "rotated" : ""}`} />
        </div>

        {isOpen && !disabled && (
          <div className="single-select-dropdown">
            {options.map((option) => (
              <div
                key={option.Id}
                className={`single-select-option ${selectedValue === option.Id ? "selected" : ""}`}
                onClick={() => handleSelectOption(option)}
              >
                <span>{option.Name}</span>
                {selectedValue === option.Id && <div className="option-checkmark">✓</div>}
              </div>
            ))}
          </div>
        )}
      </div>
      {isTags == true && selectedValue && (
        <div className="selected-tag-container">
          <div className="selected-tag">
            <span className="tag-text">{selectedOption?.Name}</span>
            {!disabled && (
              <button
                type="button"
                className="tag-remove"
                onClick={handleRemoveSelection}
                aria-label={`Remove ${selectedOption?.Name}`}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      )}

      {errors[name] && <div className="error-message">{errors[name]?.message as string}</div>}

      {/* Hidden input for react-hook-form */}
      <input type="hidden" name={name} ref={ref} disabled={disabled} {...registerProps} {...rest} />
    </div>
  )
}
